/**
 * Aurora accent engine — shade derivation from a single base hex.
 *
 * Implements §3 of `K-OS Vault/Patterns/k-os-aurora-design-system.md`: given
 * one base colour and the active theme, derive the four accent tokens
 * (`--accent`, `--accent-strong`, `--accent-soft`, `--accent-ink`) plus the
 * on-accent text colour (white / dark ink flip).
 *
 * Pure, dependency-free, DOM-free — the same maths runs in the browser hook
 * (`useAppAccent`) and in `verifyAccentDefaults()` (the self-check). Components
 * never call this directly; they read `var(--accent*)` which the hook stamps.
 */

export type AccentTheme = 'light' | 'dark';

export interface AccentShades {
  /** `--accent` — the base (dark theme may lighten a very-dark base). */
  a: string;
  /** `--accent-strong` — hover / active. */
  strong: string;
  /** `--accent-soft` — tint background. */
  soft: string;
  /** `--accent-ink` — text on `--accent-soft`. */
  ink: string;
}

const PLATFORM_DEFAULT_HEX = '#5b5bd6';

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(hi, Math.max(lo, v));

// ─── Colour-space conversions ─────────────────────────────────────────────────

interface Rgb {
  r: number;
  g: number;
  b: number;
}
interface Hsl {
  h: number; // 0–360
  s: number; // 0–100
  l: number; // 0–100
}

/** Parse `#rgb` / `#rrggbb` (with or without `#`) → 0–255 channels, or null. */
export function hexToRgb(hex: string): Rgb | null {
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const to = (v: number) =>
    clamp(Math.round(v), 0, 255).toString(16).padStart(2, '0');
  return `#${to(r)}${to(g)}${to(b)}`;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    switch (max) {
      case rn:
        h = ((gn - bn) / d) % 6;
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  const l = (max + min) / 2;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return { h, s: s * 100, l: l * 100 };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const hp = h / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0];
  else if (hp < 2) [r1, g1, b1] = [x, c, 0];
  else if (hp < 3) [r1, g1, b1] = [0, c, x];
  else if (hp < 4) [r1, g1, b1] = [0, x, c];
  else if (hp < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = ln - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

function hsl(h: number, s: number, l: number): string {
  return rgbToHex(hslToRgb({ h, s: clamp(s, 0, 100), l: clamp(l, 0, 100) }));
}

// ─── Contrast (WCAG) ──────────────────────────────────────────────────────────

function relLuminance({ r, g, b }: Rgb): number {
  const lin = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio (1–21) between two hex colours. Unknown input → 1. */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  if (!a || !b) return 1;
  const la = relLuminance(a);
  const lb = relLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

// ─── Derivation (§3) ──────────────────────────────────────────────────────────

const ON_ACCENT_DARK_INK = '#171717'; // Aurora --ink; used when accent is light
const ON_ACCENT_WHITE = '#ffffff';
/** Contrast floor for white-on-accent before we flip to a dark ink (§3). */
const ON_ACCENT_MIN_CONTRAST = 3;

/**
 * Text colour to sit on a solid `--accent` fill. White when the accent is dark
 * enough (contrast ≥ ~3:1), otherwise a dark ink. Never assumes white (§3).
 */
export function onAccentText(baseHex: string): string {
  const white = contrastRatio(ON_ACCENT_WHITE, baseHex);
  if (white >= ON_ACCENT_MIN_CONTRAST) return ON_ACCENT_WHITE;
  return ON_ACCENT_DARK_INK;
}

/**
 * Derive the four accent shades from one base hex for the given theme (§3).
 * Near-neutral inputs (`s < 8`) get the grey guard — no hue cast. Invalid
 * input falls back to the Indigo platform default.
 */
export function deriveAccent(baseHex: string, theme: AccentTheme): AccentShades {
  const rgb = hexToRgb(baseHex) ?? hexToRgb(PLATFORM_DEFAULT_HEX)!;
  const { h, s, l } = rgbToHsl(rgb);
  const gray = s < 8;

  if (theme === 'light') {
    return {
      a: rgbToHex(rgb),
      strong: hsl(h, gray ? s : clamp(s + 4, 0, 100), clamp(l - 9, 12, 86)),
      soft: hsl(h, gray ? 0 : clamp(s * 0.5, 14, 50), gray ? 94 : 93),
      ink: hsl(h, gray ? 0 : clamp(s + 12, 46, 90), gray ? 26 : 29),
    };
  }

  // Dark ground: recompute soft/ink for a dark tint; lighten a very-dark base
  // so the accent stays visible on the dark canvas.
  const veryDark = l < 45;
  const aL = veryDark ? Math.max(l, 58) : l;
  return {
    a: hsl(h, s, aL),
    // Hover lightens on dark (mirror of light-theme's darken).
    strong: hsl(h, gray ? s : clamp(s + 4, 0, 100), clamp(aL + 8, 0, 92)),
    soft: hsl(h, gray ? 0 : clamp(s * 0.6, 16, 55), 20),
    ink: hsl(h, gray ? 0 : clamp(s, 30, 80), 82),
  };
}

// ─── Self-check ────────────────────────────────────────────────────────────────

export interface AccentCheck {
  name: string;
  base: string;
  theme: AccentTheme;
  inkOnSoft: number;
  onAccentContrast: number;
  ok: boolean;
}

/**
 * Sanity-check the default accent set (indigo / emerald / coral) in both
 * themes: `--accent-ink` on `--accent-soft` and on-accent text should clear a
 * ~3:1 floor. Returns per-case results; `verifyAccentDefaults()` throws on a
 * miss so it can back a test.
 */
export function checkAccentDefaults(): AccentCheck[] {
  const defaults: Array<{ name: string; base: string }> = [
    { name: 'indigo', base: '#5B5BD6' },
    { name: 'emerald', base: '#10A154' },
    { name: 'coral', base: '#F2555A' },
  ];
  const themes: AccentTheme[] = ['light', 'dark'];
  const MIN = 3;
  const out: AccentCheck[] = [];
  for (const { name, base } of defaults) {
    for (const theme of themes) {
      const d = deriveAccent(base, theme);
      const inkOnSoft = contrastRatio(d.ink, d.soft);
      const onAccentContrast = contrastRatio(onAccentText(d.a), d.a);
      out.push({
        name,
        base,
        theme,
        inkOnSoft,
        onAccentContrast,
        ok: inkOnSoft >= MIN && onAccentContrast >= MIN,
      });
    }
  }
  return out;
}

/** Throwing wrapper around {@link checkAccentDefaults} for use in a test. */
export function verifyAccentDefaults(): void {
  const results = checkAccentDefaults();
  const failed = results.filter((r) => !r.ok);
  if (failed.length > 0) {
    const detail = failed
      .map(
        (r) =>
          `${r.name}/${r.theme}: ink-on-soft=${r.inkOnSoft.toFixed(2)} on-accent=${r.onAccentContrast.toFixed(2)}`,
      )
      .join('; ');
    throw new Error(`Aurora accent contrast check failed: ${detail}`);
  }
}
